import { useQueryClient } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { CopyIcon, TrashIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { AuthorGuard } from '@/components/layout/author-guard';
import { ConfirmDeleteModal } from '@/components/modals/confirm-delete-modal';
import { useModal } from '@/components/modals/new/use-modal';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useDeleteEstate } from '@/features/estates/api/estates';
import { StatusType } from '@/features/estates/constants/status-type';
import { getEstateOptions, useEstate } from '@/features/estates-property/api/estate';
import { usePublishEstate } from '@/features/estates-property/api/publish-estate';
import { useUnpublishEstate } from '@/features/estates-property/api/unpublish-estate';
import { CopyEstateModal } from '../../../components/modals/copy-estate-modal';

const estateRoute = getRouteApi(
  '/_authenticated/_sidebar-layout/estates/$estateId/_dashboard-layout'
);

export const EstateTopRightFunction = () => {
  const { t } = useTranslation();
  const { estateId } = estateRoute.useParams();
  const { data: estate } = useEstate({ id: estateId });
  const queryClient = useQueryClient();
  const navigate = estateRoute.useNavigate();
  const deleteModal = useModal();
  const copyModal = useModal();

  const { mutate: publishEstate } = usePublishEstate({
    mutationConfig: {
      onSuccess: () => {
        toast.success(t('publish-estate-success'));
        queryClient.invalidateQueries({ queryKey: getEstateOptions(estateId).queryKey });
      },
    },
  });
  const { mutate: unpublishEstate } = useUnpublishEstate({
    mutationConfig: {
      onSuccess: () => {
        toast.success(t('unpublish-estate-success'));
        return queryClient.invalidateQueries({ queryKey: getEstateOptions(estateId).queryKey });
      },
    },
  });

  const { mutate: deleteEstate } = useDeleteEstate({
    mutationConfig: {
      onSuccess: () => {
        toast.success(t('deleteEstateSuccess'));
        navigate({ to: '/estates' });
      },
      onError: (err) => {
        toast.error(err.message);
      },
    },
  });

  const handleChangeSwitch = (checked: boolean) => {
    if (checked) {
      publishEstate(estateId);
    } else {
      unpublishEstate(estateId);
    }
  };

  return (
    <AuthorGuard metadata={estate?.metadata}>
      <div className="flex justify-end gap-8">
        <Switch
          label={t('publish')}
          checked={estate.status === StatusType.published}
          onCheckedChange={handleChangeSwitch}
        />
        <div className="flex gap-3">
          <Button size="sm" variant="outline" className="h-8 w-8" onClick={deleteModal.open}>
            <TrashIcon />
          </Button>
          <Button size="sm" variant="outline" className="h-8 w-8" onClick={copyModal.open}>
            <CopyIcon />
          </Button>
        </div>
      </div>
      <ConfirmDeleteModal
        modal={deleteModal}
        onSubmit={() => deleteEstate(estateId)}
        title={t('deleteEstate')}
        description={t('confirmDeleteEstate')}
      />
      <CopyEstateModal estateId={estateId} navigate={navigate} modal={copyModal} />
    </AuthorGuard>
  );
};
